import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Client } from "@shared/schema";
import type { SessionUser } from "@/lib/auth";

const STORAGE_KEY = "kintsugi_viewAsClientId";

function readStoredClientId(): number | undefined {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v) return Number(v);
  } catch {}
  return undefined;
}

type ClientViewState = {
  viewAsClientId: number | undefined;
  viewAsClient: Client | undefined;
  setViewAsClientId: (id: number | undefined) => void;
  isViewingAsClient: boolean;
  effectiveIsAdmin: boolean;
  effectiveShowRent: boolean;
  effectiveClientId: number | undefined;
};

const ClientViewContext = createContext<ClientViewState>({
  viewAsClientId: undefined,
  viewAsClient: undefined,
  setViewAsClientId: () => {},
  isViewingAsClient: false,
  effectiveIsAdmin: false,
  effectiveShowRent: false,
  effectiveClientId: undefined,
});

export function ClientViewProvider({ children }: { children: React.ReactNode }) {
  const [viewAsClientId, setViewAsClientIdRaw] = useState<number | undefined>(readStoredClientId);

  const setViewAsClientId = useCallback((id: number | undefined) => {
    setViewAsClientIdRaw(id);
    try {
      if (id !== undefined) {
        sessionStorage.setItem(STORAGE_KEY, String(id));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
  }, []);

  const { data: user } = useQuery<SessionUser | null>({
    queryKey: ["/api/auth/me"],
    queryFn: () => fetch("/api/auth/me", { credentials: "include" }).then((r) => {
      if (!r.ok) return null;
      return r.json();
    }),
    staleTime: 60_000,
  });

  const { data: clients } = useQuery<Client[]>({ queryKey: ["/api/clients"] });

  const value = useMemo(() => {
    const realAdmin = user?.role === "admin" || user?.realUser?.role === "admin";
    const isUserImpersonating = user?.isImpersonating ?? false;

    const isViewingAsClient = realAdmin && !isUserImpersonating && viewAsClientId !== undefined;
    const viewAsClient = viewAsClientId ? clients?.find((c) => c.id === viewAsClientId) : undefined;

    const effectiveIsAdmin = realAdmin && !isUserImpersonating && !isViewingAsClient;

    const myClient = user?.clientId ? clients?.find((c) => c.id === user.clientId) : null;

    let effectiveShowRent: boolean;
    if (isViewingAsClient && viewAsClient) {
      effectiveShowRent = viewAsClient.type === "Rental";
    } else if (effectiveIsAdmin) {
      effectiveShowRent = true;
    } else {
      effectiveShowRent = (myClient?.type === "Rental") || false;
    }

    let effectiveClientId: number | undefined;
    if (isViewingAsClient) {
      effectiveClientId = viewAsClientId;
    } else if (!realAdmin || isUserImpersonating) {
      effectiveClientId = user?.clientId ?? undefined;
    } else {
      effectiveClientId = undefined;
    }

    return {
      viewAsClientId,
      viewAsClient,
      setViewAsClientId,
      isViewingAsClient,
      effectiveIsAdmin,
      effectiveShowRent,
      effectiveClientId,
    };
  }, [viewAsClientId, user, clients]);

  return (
    <ClientViewContext.Provider value={value}>
      {children}
    </ClientViewContext.Provider>
  );
}

export function useClientView() {
  return useContext(ClientViewContext);
}
